<template>
  <div class="caixa-form" data-testid="caixa-form">
    <!-- MODO: SANGRIA / SUPRIMENTO / AJUSTE POSITIVO / AJUSTE NEGATIVO / DESPESA -->
    <template v-if="modo === 'sangria' || modo === 'suprimento' || modo === 'ajuste_positivo' || modo === 'ajuste_negativo' || modo === 'despesa_dinheiro'">
      <div class="info-box" style="padding: 12px; background: var(--surface-ground); border-radius: 8px; margin-bottom: 16px;">
        <div style="display: flex; justify-content: space-between;">
          <span>{{ t('Saldo Atual') }}</span>
          <strong :class="saldoAtual >= 0 ? 'text-success' : 'text-danger'">
            {{ n(saldoAtual, 'currency') }}
          </strong>
        </div>
        <div v-if="modo === 'sangria'" style="display: flex; justify-content: space-between; margin-top: 4px;">
          <span>{{ t('Saldo após sangria') }}</span>
          <strong :class="(saldoAtual - (formData.valor || 0)) >= 0 ? 'text-success' : 'text-danger'">
            {{ n(saldoAtual - (formData.valor || 0), 'currency') }}
          </strong>
        </div>
      </div>

      <div class="form-field" style="margin-bottom: 16px;">
        <label>
          {{ modo === 'sangria' ? t('Valor da Sangria') : modo === 'suprimento' ? t('Valor do Suprimento') : modo === 'ajuste_positivo' ? t('Valor do Ajuste Positivo') : modo === 'ajuste_negativo' ? t('Valor do Ajuste Negativo') : t('Valor da Despesa') }} *
        </label>
        <InputCurrency
          v-model="formData.valor"
          :placeholder="t('0,00')"
          required
          data-testid="input-valor-movimentacao"
        />
      </div>

      <!-- Conta Cofre (apenas para sangria) -->
      <div v-if="modo === 'sangria'" class="form-field" style="margin-bottom: 16px;">
        <label>{{ t('Conta Destino') }}</label>
        <select-list
          v-if="cofreContas && cofreContas.length > 0"
          v-model="formData.cofre_id"
          elem-id="caixa_sangria_cofre_id"
          :itens-select="contasDoCofre"
          :auto-select-first="false"
          :placeholder="t('Selecione a conta')"
          data-testid="select-cofre-sangria"
        />
        <div v-else class="text-muted" style="padding: 8px 0;">
          {{ t('Nenhuma conta cadastrada no Cofre.') }}
        </div>
      </div>

      <div class="form-field" style="margin-bottom: 16px;">
        <label>{{ t('Observação') }}</label>
        <input-text
          v-model="formData.observacao"
          elem-id="caixa_movimentacao_observacao"
          :placeholder="modo === 'sangria' ? t('Ex: Retirada para troco') : modo === 'suprimento' ? t('Ex: Dinheiro trazido de casa') : modo === 'ajuste_positivo' ? t('Ex: Correção de valor') : modo === 'ajuste_negativo' ? t('Ex: Quebra de caixa') : t('Ex: Conta paga em espécie')"
          data-testid="input-observacao-movimentacao"
        />
      </div>
    </template>

    <!-- BOTÃO DE SUBMISSÃO -->
    <div class="form-actions" style="display: flex; justify-content: flex-end; gap: 8px; margin-top: 24px;">
      <Button
        :label="t('Cancelar')"
        severity="secondary"
        outlined
        data-testid="btn-cancelar"
        @click="$emit('cancel')"
      />
      <Button
        :label="botaoLabel"
        :icon="botaoIcon"
        :severity="botaoSeverity"
        :loading="loading"
        :disabled="!isFormValid"
        data-testid="btn-submit-caixa"
        @click="$emit('submit', formData)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps<{
  modo: 'sangria' | 'suprimento' | 'ajuste_positivo' | 'ajuste_negativo' | 'despesa_dinheiro';
  caixaAberto: any | null;
  pendentesConferencia?: number;
  totalConferencia?: number;
  saldoAtual: number;
  loading: boolean;
  cofreContas?: any[];
}>();

defineEmits<{
  submit: [data: Record<string, any>];
  cancel: [];
}>();

const { t, n } = useI18n();

// ===========================================
// FORM DATA
// ===========================================

const formData = reactive<Record<string, any>>({
  saldo_inicial: 0,
  saldo_final_declarado: null,
  valor: null,
  observacao: '',
});

// ===========================================
// COMPUTED
// ===========================================

/** As contas do cofre no formato { ID, Name } que a lista de seleção lê. */
const contasDoCofre = computed(() =>
  (props.cofreContas || []).map((c) => ({ ID: c.id, Name: c.nome })),
);

const isFormValid = computed(() => {
  switch (props.modo) {
    case 'sangria':
      return formData.valor !== null
        && formData.valor !== undefined
        && formData.valor > 0
        && !!formData.cofre_id;
    case 'suprimento':
    case 'ajuste_positivo':
    case 'ajuste_negativo':
    case 'despesa_dinheiro':
      return formData.valor !== null
        && formData.valor !== undefined
        && formData.valor > 0;
    default:
      return false;
  }
});

const botaoLabel = computed(() => {
  const map: Record<string, string> = {
    'sangria': t('Registrar Sangria'),
    'suprimento': t('Registrar Suprimento'),
    'ajuste_positivo': t('Registrar Ajuste Positivo'),
    'ajuste_negativo': t('Registrar Ajuste Negativo'),
    'despesa_dinheiro': t('Registrar Despesa'),
  };
  return map[props.modo] || t('Confirmar');
});

const botaoIcon = computed(() => {
  const map: Record<string, string> = {
    'sangria': 'pi pi-arrow-up',
    'suprimento': 'pi pi-arrow-down',
    'ajuste_positivo': 'pi pi-plus',
    'ajuste_negativo': 'pi pi-minus',
    'despesa_dinheiro': 'pi pi-receipt',
  };
  return map[props.modo] || 'pi pi-check';
});

const botaoSeverity = computed(() => {
  const map: Record<string, string> = {
    'sangria': 'danger',
    'suprimento': 'success',
    'ajuste_positivo': 'info',
    'ajuste_negativo': 'warning',
    'despesa_dinheiro': 'danger',
  };
  return map[props.modo] || 'primary';
});

// ===========================================
// HELPERS
// ===========================================
</script>

<style scoped>
.form-field label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  font-size: 0.9rem;
}

.text-muted {
  color: var(--text-color-secondary);
  font-size: 0.8rem;
  display: block;
  margin-top: 4px;
}

.text-success {
  color: var(--green-500);
}

.text-danger {
  color: var(--red-500);
}

.text-warning {
  color: var(--orange-500);
}
</style>
